import { memo, useCallback } from "react";
import { useAnalytics } from "app/utils/analytics/useAnalytics";
import PropTypes from "prop-types";
import { RESOLUTION } from "app/pages/.shared/responsive/responsiveReducer";
import BackgroundProgressiveImg from "app/pages/.shared/BackgroundProgressiveImg";
import ProgressiveImg from "app/pages/.shared/ProgressiveImg";
import { resolutionPropType } from "app/utils/propTypes";
import { useNavigate } from "react-router-dom";
import "./CurrentMerchCollectionItem.scss";
import { htmlSanitize } from "app/utils/sanitize";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import {
	buildHotelOnlySearchParamsForDestination,
	buildHotelOnlySearchTagPayload,
} from "app/utils/smartDPUtils";
import { ButtonLink } from "app/components/ui/ButtonLink/ButtonLink";
import { ArrowRightIcon } from "app/icons/navigation";
import { Amount } from "app/components/ui/Amount/Amount";
import { FormattedMessage } from "react-intl";
import { BadgeItem } from "app/components/ui/badge/BadgeItem";
import { useTemplateRenderToString } from "app/utils/hooks/useTemplateRender";

const IMG_SIZES_MOBILE = {
	small: { width: 265, height: 250 },
	medium: { width: 265, height: 250 },
};

const IMG_SIZES_DESKTOP = {
	large: { width: 404, height: 400 },
	xlarge: { width: 404, height: 400 },
};

const CLOUDINARY_OPTIONS = ["c_fill"];

const CurrentMerchCollectionItem = ({
	resolution,
	picture = {},
	lowestPrice,
	label,
	merchandisingTitle,
	merchandisingSubtitle,
	introduction,
	merchandisingCode,
	enableRedirectionToHotelOnlyListingPage,
	destinationFilters = [],
	destinations = [],
	position,
	indexCarousel,
	onClick = () => {},
	merchandisingStamp = {},
}) => {
	const { track } = useAnalytics();

	const navigate = useNavigate();
	const templateRenderToString = useTemplateRenderToString();

	let merchRedirectUrlData = {
		pathname: `/merch/${merchandisingCode}`,
	};

	const shouldNavigateToListing =
		enableRedirectionToHotelOnlyListingPage && destinationFilters?.length > 0;

	const destinationResort = shouldNavigateToListing
		? destinations.find(destination => destination.code === Number(destinationFilters[0]?.id))
		: undefined;

	if (destinationResort) {
		merchRedirectUrlData = {
			pathname: "/hotelonly/listing",
			search: `?${buildHotelOnlySearchParamsForDestination(destinationResort?.code)}`,
		};
	}

	// handle click on image
	const navigateToMerch = useCallback(() => {
		if (destinationResort) {
			track("sdp_search_submit", buildHotelOnlySearchTagPayload(destinationResort.code));
		}
		navigate(merchRedirectUrlData);
		onClick({ merchandisingCode, position, indexCarousel });
	}, [
		merchandisingCode,
		position,
		indexCarousel,
		merchRedirectUrlData,
		destinationResort,
		track,
	]);

	const handleMerchClick = useCallback(
		e => {
			e.preventDefault(); // avoid double navigate because and double entries in history there is already one on upper div
			onClick({ merchandisingCode, position, indexCarousel });
		},
		[merchandisingCode, position, indexCarousel]
	);

	const showPrice = merchandisingStamp?.hideLowestPrice !== true && lowestPrice > 0;

	return (
		<div
			className="current-merch-collection-item"
			data-testid={`current-merch-item-${merchandisingCode}`}
		>
			<div onClick={navigateToMerch}>
				<div className="current-merch-collection-item__picture">
					{showPrice && merchandisingStamp?.showLabel && (
						<div className="current-merch-collection-item__badge">
							<BadgeItem type="priceDrop" />
						</div>
					)}

					{resolution === RESOLUTION.MEDIUM || resolution === RESOLUTION.SMALL ? (
						<BackgroundProgressiveImg
							sizes={IMG_SIZES_MOBILE}
							cloudinaryOptions={CLOUDINARY_OPTIONS}
							src={picture.url}
							alt={label}
							thumbnail={picture.thumbnail}
						/>
					) : (
						<ProgressiveImg
							className="progressive-img"
							sizes={IMG_SIZES_DESKTOP}
							src={picture.url}
							alt={label}
							thumbnail={picture.thumbnail}
						/>
					)}
				</div>

				<div className="current-merch-collection-item__header">
					<div className="current-merch-collection-item__header-left">
						<Typography variant={TYPOGRAPHY_VARIANTS.LARGE} isBold>
							<div
								className="current-merch-collection-item__label"
								dangerouslySetInnerHTML={{
									__html: htmlSanitize(
										templateRenderToString(merchandisingTitle || label)
									),
								}}
							/>
						</Typography>

						<div
							className="current-merch-collection-item__intro"
							dangerouslySetInnerHTML={{
								__html: htmlSanitize(
									templateRenderToString(merchandisingSubtitle || introduction)
								),
							}}
						/>
					</div>
				</div>

				<div className="current-merch-collection-item__footer">
					{showPrice && (
						<Amount value={lowestPrice} from perPerson data-testid="amount" />
					)}

					<ButtonLink
						variant="primary"
						size={showPrice ? "icon-sm" : "sm"}
						fullWidth={!showPrice}
						endIcon={<ArrowRightIcon />}
						className="current-merch-collection-item__cta"
						to={merchRedirectUrlData}
						onClick={handleMerchClick}
						data-testid={`merch-cta-${merchandisingCode}`}
					>
						{!showPrice && <FormattedMessage id="current.merch.collection.cta.label" />}
					</ButtonLink>
				</div>
			</div>
		</div>
	);
};

CurrentMerchCollectionItem.propTypes = {
	label: PropTypes.string,
	picture: PropTypes.object,
	introduction: PropTypes.string,
	resolution: resolutionPropType,
	position: PropTypes.number,
	enableRedirectionToHotelOnlyListingPage: PropTypes.bool,
	destinationFilters: PropTypes.array,
	destinations: PropTypes.array,
	indexCarousel: PropTypes.number,
	merchandisingCode: PropTypes.string,
	merchandisingTitle: PropTypes.string,
	merchandisingSubtitle: PropTypes.string,
	lowestPrice: PropTypes.number,
	onClick: PropTypes.func,
	merchandisingStamp: PropTypes.object,
};

export default memo(CurrentMerchCollectionItem);
